import React, { useState, useEffect, useCallback } from 'react';

import { TextField, Autocomplete } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { ElementProps } from '../../../@types/forms/types';
import { getCustomListById } from '../../../services/formService';
import { CustomListProps } from '../edit-form/element-settings/types/elementSettingsTypes';

//-------------------------------------------------------------

type CustomListElementProps = {
  el: ElementProps;
  scaledDimensions: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    fontSize: number;
  };
  elementContentValue?: string[];
  handleTextAlign: (alignEnum: number) => string;
  handleDropdownChange?: (value: string, el: ElementProps) => void;
  pageNumberScale: number;
  collaboratorColor?: string;
  disabled?: boolean;
  errorMessage?: string;
};

const CustomListElement: React.FC<CustomListElementProps> = ({
  el,
  scaledDimensions,
  elementContentValue,
  handleTextAlign,
  handleDropdownChange,
  pageNumberScale,
  collaboratorColor,
  disabled = false,
  errorMessage,
}) => {
  const [customListOptions, setCustomListOptions] = useState<string[]>([]);
  const [value, setValue] = useState<string | undefined>(undefined);

  const getListById = useCallback(async () => {
    if (el.list) {
      try {
        const list = (await getCustomListById(el.list.id)) as CustomListProps;
        if (list.options) {
          setCustomListOptions(list.options);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, [el.list]);

  useEffect(() => {
    getListById();
  }, [getListById]);

  useEffect(() => {
    if (elementContentValue && elementContentValue.length > 0) {
      setValue(elementContentValue[0]);
    } else {
      setValue(undefined);
    }
  }, [elementContentValue]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      handleDropdownChange?.(newValue, el);
      setValue(newValue);
    },
    [handleDropdownChange, el]
  );

  return (
    <Autocomplete
      popupIcon={<ExpandMoreRoundedIcon />}
      value={value || ''}
      options={customListOptions}
      disableClearable
      disabled={disabled}
      id={el.id}
      onChange={handleChange}
      sx={[
        {
          backgroundColor: collaboratorColor,
          borderRadius: 1,
          width: '100%',
          '& input': {
            color: el.color,
            fontSize: scaledDimensions.fontSize,
            textAlign: handleTextAlign(el.textAlignment),
            fontWeight: el.textStyle.includes('Bold') ? 'Bold' : 'Regular',
            fontStyle: el.textStyle.includes('Italic') ? 'italic' : 'normal',
            fontFamily: el.font,
          },
        },
        el.textDecoration === 1
          ? { '& input': { textDecoration: 'underline' } }
          : { '& input': { textDecoration: '' } },
      ]}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          error={!!errorMessage}
          helperText={errorMessage || null}
          id="wcei5xzk"
          sx={{ width: '100%' }}
        />
      )}
    />
  );
};

export default CustomListElement;
